// Espace employé — self-service page (soft-island port of the design
// prototype "Espace Employé.html", es-* files). Rendered OUTSIDE the
// admin AppShell: employees signing in with their email land here and
// can go nowhere else (see requireStaff server-side + the redirects
// in App.tsx). The island stacks `esapp` on top of the congés island
// base so tokens/modals/calendars come from conges.css.
//
// Deviations from the prototype (deliberate):
// - no demo dataset / employee switcher / Tweaks panel — real data
//   only, scoped to the signed-in employee via /api/me,
// - no team calendar or "également absents" hints (an employee must
//   not see the whole team's absences),
// - documents: no payslip depository exists, so fiches de paie go
//   through the RH request flow; the attestation de travail scan is
//   a direct server-generated PDF,
// - the "sans solde / maladie" request types stay staff-managed in
//   Saisie quotidienne; employees request congé or récup.
import { useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router';
import {
  useLogout, useMe, useMeChart, useMeOverview, useMeRequests, useMyEvaluation,
  useCancelMyClaim, useCancelMyDocRequest, useCancelMyLeaveRequest,
  type LeaveType, type MeLeaveRequestDTO,
} from '../api/hooks.js';
import { useUiStore } from '../store/ui.js';
import { useUsageHeartbeat } from '../lib/usageHeartbeat.js';
import { toast } from '../store/toast.js';
import { cgFmtLong, cgInitials, cgAvColor, cgNum, cgToday } from '../components/conges/common.js';
import {
  ES_SERVICES, ES_TONE, EsMiniChart, EsRing, LEAVE_TYPE_LBL, monthlyCurve, type ServiceKey,
} from '../components/espace/common.js';
import { buildItems, EsReqRow, type EsItem } from '../components/espace/EsRequestsList.js';
import { EsDocsView } from '../components/espace/EsDocsView.js';
import { EsRequestModal } from '../components/espace/EsRequestModal.js';
import { EsClaimModal } from '../components/espace/EsClaimModal.js';
import { EsDocModal } from '../components/espace/EsDocModal.js';
import { CgCount } from '../components/conges/common.js';
import { NotificationBell } from '../components/NotificationBell.js';
import { RefreshButton } from '../components/RefreshButton.js';
import { EsEvaluation, esEvalBadge } from '../components/espace/EsEvaluation.js';
import '../styles/conges.css';
import '../styles/espace-employe.css';
import '../styles/evaluations.css';

type Tab = 'accueil' | 'demandes' | 'evaluation' | 'documents';
type Filter = 'encours' | 'traitees' | 'toutes';

const todayLabel = () =>
  new Intl.DateTimeFormat('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }).format(new Date());

export function EspaceEmployePage() {
  const me = useMe();
  const theme = useUiStore((s) => s.theme);
  const toggleTheme = useUiStore((s) => s.toggleTheme);
  const logout = useLogout();
  // Same heartbeat as the back-office shell — it is what answers
  // "l'espace employé est-il vraiment utilisé ?".
  useUsageHeartbeat(Boolean(me.data));

  // Staff can open their own espace too ("Mon espace" in the sidebar):
  // /api/me resolves their fiche by email. The follow-up queries only
  // fire once the fiche is confirmed to exist.
  const isEmployee = me.data?.user.role === 'EMPLOYEE';
  const overview = useMeOverview(!!me.data);
  const hasFiche = !!overview.data;
  const requests = useMeRequests(isEmployee || hasFiche);
  const year = Number(cgToday().slice(0, 4));
  const chart = useMeChart(year, isEmployee || hasFiche);
  const myEval = useMyEvaluation(year, isEmployee || hasFiche);

  const [tab, setTab] = useState<Tab>('accueil');
  const [filter, setFilter] = useState<Filter>('encours');
  const [openItem, setOpenItem] = useState<string | null>(null);
  const [leaveModal, setLeaveModal] = useState<{ type?: LeaveType } | null>(null);
  const [claimModal, setClaimModal] = useState(false);
  const [docModal, setDocModal] = useState<ServiceKey | null>(null);

  const cancelLeave = useCancelMyLeaveRequest();
  const cancelClaim = useCancelMyClaim();
  const cancelDoc = useCancelMyDocRequest();

  const items = useMemo(() => buildItems(requests.data), [requests.data]);
  const live = items.filter((i) => i.live);
  const shown = items.filter((i) => (filter === 'toutes' ? true : filter === 'encours' ? i.live : !i.live));
  const readyDocs = (requests.data?.docs ?? []).filter((d) => d.status === 'PRETE').length;

  const today = cgToday();
  const validated = useMemo(
    () => (requests.data?.leaves ?? []).filter((l) => l.stage === 'APPROVED' || l.stage === 'DEBITED'),
    [requests.data],
  );
  const onNow = validated.find((l) => l.startDate <= today && l.endDate >= today) ?? null;
  const nextLeave = validated.filter((l) => l.startDate > today).sort((a, b) => (a.startDate < b.startDate ? -1 : 1))[0] ?? null;

  // Guards AFTER all hooks: signed-out → login. Staff without a fiche
  // employé (email not matching any active employee) get an explainer
  // instead of a broken space.
  if (me.isLoading) return null;
  if (!me.data) return <Navigate to="/login" replace />;
  if (!isEmployee && !overview.isLoading && overview.data === null) {
    return (
      <div className={'cgapp esapp' + (theme === 'dark' ? ' theme-dark' : '')}>
        <div className="es-aurora" />
        <div className="es-nav">
          <div className="es-brand"><span className="k">Pointage</span><span className="t">Espace employé</span></div>
          <div className="es-navspace" />
          <Link className="es-navbtn" to="/dashboard">← Back-office</Link>
        </div>
        <div className="es-scroll">
          <div className="es-wrap">
            <div className="es-pagehead" style={{ marginTop: 24 }}>
              <div>
                <div className="gm-eyebrow">Mon espace</div>
                <h1 className="gm-title sm">Aucune fiche employé liée</h1>
                <p className="gm-sub">
                  Votre compte <b>{me.data.user.email}</b> n'est relié à aucune fiche employé active.
                  Pour accéder à votre espace (soldes, demandes, documents), renseignez cette adresse
                  email sur votre propre fiche dans <Link to="/employees">Employés</Link>, puis revenez ici.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const o = overview.data;
  const conge = o?.balances.conge;
  const recup = o?.balances.recup;
  const congeCur = conge?.current ?? 0;
  const recupCur = recup?.current ?? 0;
  const congeTotal = (conge?.initialStock ?? 0) + (conge?.accrued ?? 0) + (conge?.manualAdded ?? 0);
  const recupTotal = (recup?.initialStock ?? 0) + (recup?.accrued ?? 0) + (recup?.manualAdded ?? 0);
  const curve = monthlyCurve(chart.data?.events ?? [], year, conge?.initialStock ?? 0, today);
  const recentClaims = (requests.data?.claims ?? []).slice(0, 3);

  const openService = (k: ServiceKey) => {
    const svc = ES_SERVICES[k];
    if (svc.kind === 'leave') setLeaveModal({ type: k === 'recup' ? 'RECUP' : 'CONGE' });
    else if (svc.kind === 'claim') setClaimModal(true);
    else setDocModal(k);
  };

  const cancelItem = (it: EsItem) => {
    const done = { onSuccess: () => toast.success('Demande annulée.'), onError: () => toast.danger("Impossible d'annuler cette demande.") };
    if (it.k === 'l') cancelLeave.mutate(it.id, done);
    else if (it.k === 'c') cancelClaim.mutate(it.id, done);
    else cancelDoc.mutate(it.id, done);
  };

  const TABS: Array<[Tab, string]> = [['accueil', 'Accueil'], ['demandes', 'Mes demandes'], ['evaluation', 'Mon évaluation'], ['documents', 'Mes documents']];
  const evalBadge = esEvalBadge(myEval.data);

  return (
    <div className={'cgapp esapp' + (theme === 'dark' ? ' theme-dark' : '')}>
      <div className="es-aurora" />
      <div className="es-nav">
        <div className="es-brand"><span className="k">Pointage</span><span className="t">Espace employé</span></div>
        <div className="es-tabs">
          {TABS.map(([k, l]) => (
            <button key={k} className={tab === k ? 'on' : ''} onClick={() => setTab(k)}>
              {l}
              {k === 'demandes' && live.length > 0 && <em>{live.length}</em>}
              {k === 'evaluation' && evalBadge > 0 && <em>{evalBadge}</em>}
              {k === 'documents' && readyDocs > 0 && <em className="g">{readyDocs}</em>}
            </button>
          ))}
        </div>
        <div className="es-navspace" />
        <div className="es-who">
          {o && <span className="gm-av" style={{ background: cgAvColor(o.employee.id) }}>{cgInitials(o.employee.name)}</span>}
          <span className="es-who-m">
            <b>{o?.employee.name ?? me.data.user.name}</b>
            <i>{o ? `${o.employee.department ?? o.employee.code} · ${o.employee.entity.short}` : me.data.user.email}</i>
          </span>
        </div>
        <RefreshButton className="es-theme" />
        <NotificationBell me={me.data} />
        <button className="es-theme" onClick={toggleTheme} title="Basculer le thème">{theme === 'dark' ? '☀' : '☾'}</button>
        {!isEmployee && <Link className="es-navbtn" to="/dashboard">← Back-office</Link>}
        <button className="es-navbtn" onClick={() => logout.mutate(undefined, { onSuccess: () => { window.location.href = '/login'; } })}>
          Déconnexion
        </button>
      </div>

      <div className="es-scroll">
        <div className="es-wrap" key={tab}>
          {tab === 'accueil' && o && (<>
            <div className="es-hero">
              <div>
                <div className="gm-eyebrow">{todayLabel()} · {o.employee.department ?? o.employee.code} · {o.employee.entity.name}</div>
                <h1 className="gm-title">Bonjour, <em>{o.employee.name}</em></h1>
                <p className="gm-sub">
                  {onNow
                    ? `Vous êtes en ${LEAVE_TYPE_LBL[onNow.type].lbl.toLowerCase()} jusqu'au ${cgFmtLong(onNow.endDate)}.`
                    : nextLeave
                      ? `Prochaine absence validée le ${cgFmtLong(nextLeave.startDate)}.`
                      : 'Aucune absence planifiée pour le moment.'}
                  {' '}Tout se demande ici : congés, récup, attestations et documents RH.
                </p>
                <div className="es-chips">
                  <span className={'es-statchip' + (onNow ? ' on' : '')}>
                    <i className="es-live" />{onNow ? LEAVE_TYPE_LBL[onNow.type].lbl : "Au travail aujourd'hui"}
                  </span>
                  {live.length > 0 && (
                    <button className="es-statchip warn" onClick={() => setTab('demandes')}>
                      ⏳ {live.length} demande{live.length > 1 ? 's' : ''} en cours
                    </button>
                  )}
                  {readyDocs > 0 && (
                    <button className="es-statchip green" onClick={() => setTab('documents')}>
                      ✓ {readyDocs} document{readyDocs > 1 ? 's' : ''} à retirer
                    </button>
                  )}
                  <button className="es-statchip" onClick={() => setTab('documents')}>Attestation de travail en 1 clic</button>
                </div>
              </div>
              <div className="es-hero-r">
                <span className="es-hero-k">Solde disponible</span>
                <span className="es-hero-v gm-mono"><CgCount value={congeCur + recupCur} suffix="j" /></span>
                <span className="es-hero-s">{cgNum(congeCur)} j de congés · {cgNum(recupCur)} j de récup</span>
              </div>
            </div>

            <div className="es-svc-head">
              <span className="es-secttl">Que souhaitez-vous faire ?</span>
            </div>
            <div className="es-svcs">
              {(Object.keys(ES_SERVICES) as ServiceKey[]).map((k) => {
                const svc = ES_SERVICES[k];
                const [bg, fg] = ES_TONE[svc.tone];
                const disabled = k === 'recup' && recupCur <= 0;
                const hint =
                  k === 'conge' ? `${cgNum(congeCur)} j disponibles sur votre solde annuel`
                    : k === 'recup' ? (recupCur > 0 ? `${cgNum(recupCur)} j de récup disponibles` : 'Aucun jour de récup disponible')
                      : svc.sub;
                return (
                  <button key={k} className="es-svc" disabled={disabled} onClick={() => openService(k)}>
                    <span className="es-svc-ic" style={{ background: bg, color: fg }}>{svc.ic}</span>
                    <b>{svc.lbl}</b>
                    <i>{hint}</i>
                    <span className="es-svc-go">→</span>
                  </button>
                );
              })}
            </div>

            <div className="es-bals">
              <div className="es-bal">
                <EsRing value={congeCur} total={Math.max(congeTotal, 1)} color="var(--shf)" />
                <div className="es-bal-m">
                  <span className="es-bal-l">Congés annuels</span>
                  <span className="es-bal-v gm-mono"><CgCount value={congeCur} suffix="jours" /></span>
                  <span className="es-bal-s">
                    {cgNum(conge?.initialStock ?? 0)} report · {cgNum((conge?.accrued ?? 0) + (conge?.manualAdded ?? 0))} acquis · {cgNum(conge?.consumed ?? 0)} pris
                  </span>
                  <EsMiniChart pts={curve} color="var(--shf)" />
                  <span className="es-bal-tag">{o.accrualRule}</span>
                </div>
              </div>
              <div className="es-bal recup">
                <EsRing value={recupCur} total={Math.max(recupTotal, 1)} color="var(--info)" />
                <div className="es-bal-m">
                  <span className="es-bal-l">Récupération</span>
                  <span className="es-bal-v gm-mono"><CgCount value={recupCur} suffix="jours" /></span>
                  <span className="es-bal-s">{cgNum(recupTotal)} j crédités · {cgNum(recup?.consumed ?? 0)} j pris</span>
                  <div className="es-recupsrc">
                    {recentClaims.map((c) => (
                      <span key={c.id} className={'es-src' + (c.stage === 'PENDING' ? ' pend' : '')}>
                        {cgFmtLong(c.date).replace(` ${year}`, '')} <b>+{String(c.amount).replace('.', ',')}</b>
                      </span>
                    ))}
                    {recentClaims.length === 0 && <span className="es-src empty">Aucun week-end travaillé déclaré</span>}
                  </div>
                  <span className="es-bal-tag info">1 week-end ou férié travaillé = 1 jour</span>
                </div>
              </div>
            </div>

            <div className="es-listhead">
              <span className="es-secttl">Suivi en cours</span>
              <button className="es-dv-act" onClick={() => setTab('demandes')}>Tout l'historique →</button>
            </div>
            <div className="es-list">
              {requests.isLoading && <div className="cg-placeholder">Chargement…</div>}
              {!requests.isLoading && live.length === 0 && <div className="cg-empty">Aucune demande en cours. Tout est à jour.</div>}
              {live.slice(0, 5).map((it) => (
                <EsReqRow key={it.id} it={it} open={openItem === it.id}
                  onToggle={() => setOpenItem(openItem === it.id ? null : it.id)} onCancel={cancelItem} />
              ))}
            </div>
          </>)}

          {tab === 'accueil' && !o && (
            <div className="cg-placeholder" style={{ marginTop: 40 }}>
              {overview.isLoading ? 'Chargement de votre espace…' : "Impossible de charger votre espace. Rechargez la page."}
            </div>
          )}

          {tab === 'demandes' && (<>
            <div className="es-pagehead">
              <div>
                <div className="gm-eyebrow">Historique complet</div>
                <h1 className="gm-title sm">Mes demandes</h1>
                <p className="gm-sub">
                  Congés, récupérations et documents administratifs — {items.length} demande{items.length > 1 ? 's' : ''}, {live.length} en cours de traitement.
                </p>
              </div>
              <div className="es-quick">
                {(['conge', 'recup', 'jour', 'attestation', 'paie'] as ServiceKey[]).map((k) => (
                  <button key={k} className="es-qbtn" onClick={() => openService(k)}>{ES_SERVICES[k].ic} {ES_SERVICES[k].lbl}</button>
                ))}
              </div>
            </div>
            <div className="es-listhead">
              <div className="es-filters">
                {([['encours', 'En cours'], ['traitees', 'Traitées'], ['toutes', 'Toutes']] as Array<[Filter, string]>).map(([k, l]) => (
                  <button key={k} className={filter === k ? 'on' : ''} onClick={() => setFilter(k)}>{l}</button>
                ))}
              </div>
            </div>
            <div className="es-list">
              {shown.length === 0 && <div className="cg-empty">Aucune demande dans cette vue.</div>}
              {shown.map((it) => (
                <EsReqRow key={it.id} it={it} open={openItem === it.id}
                  onToggle={() => setOpenItem(openItem === it.id ? null : it.id)} onCancel={cancelItem} />
              ))}
            </div>
          </>)}

          {tab === 'evaluation' && o && <EsEvaluation overview={o} year={year} />}

          {tab === 'documents' && (
            <EsDocsView docs={requests.data?.docs ?? []} onRequest={(k) => openService(k)} />
          )}
        </div>
      </div>

      {leaveModal && o && (
        <EsRequestModal overview={o} preset={leaveModal} onClose={() => setLeaveModal(null)}
          onSent={() => { setLeaveModal(null); setTab('demandes'); setFilter('encours'); toast.success('Demande transmise à votre responsable.'); }} />
      )}
      {claimModal && (
        <EsClaimModal onClose={() => setClaimModal(false)}
          onSent={() => { setClaimModal(false); setTab('demandes'); setFilter('encours'); toast.success('Journée travaillée déclarée.'); }} />
      )}
      {docModal && o && ES_SERVICES[docModal].doc && (
        <EsDocModal overview={o} type={ES_SERVICES[docModal].doc!} validatedLeaves={validated as MeLeaveRequestDTO[]}
          onClose={() => setDocModal(null)}
          onSent={() => { setDocModal(null); setTab('demandes'); setFilter('encours'); toast.success('Demande de document envoyée à la RH.'); }} />
      )}
    </div>
  );
}
